<template>
  <div>
    <v-navigation-drawer
      :value="isAddEditOtherProductsSidebar"
      temporary
      touchless
      :stateless="alertGuard"
      floating
      right
      app
      :width="$vuetify.breakpoint.xsOnly ? '100%' : 450"
      :class="$vuetify.breakpoint.xsOnly ? 'rounded-0' : 'rounded-l-xl'"
      @input="val => $emit('update:is-add-edit-other-products-sidebar', val)"
    >
      <v-card flat tile>
        <v-card-title class="px-4 thead-color">
          <v-avatar size="50" color="primary" class="mr-3">
            <v-icon color="white" :size="30">
              {{ icons.mdiEyePlus }}
            </v-icon>
          </v-avatar>
          <span class="font-weight-semibold text-base primary--text">Solüsyon Ekle</span>

          <v-spacer></v-spacer>
          <v-btn icon small @click="closeSidebar">
            <v-icon size="22">
              {{ icons.mdiClose }}
            </v-icon>
          </v-btn>
        </v-card-title>
        <ValidationObserver ref="form" tag="div">
          <form @submit.prevent>
            <ValidationProvider v-slot="{ errors }" rules="required">
              <v-autocomplete
                v-model="formField.name"
                :items="items.name"
                :readonly="!formField.reverse"
                label="Ürün Adı"
                item-text="stext"
                item-value="stext"
                return-object
                class="rounded-0"
                hide-details
                attach
                eager
                filled
                :error-messages="errors"
                :menu-props="{ bottom: true, offsetY: true }"
                @focus="nameFocus()"
                @change="nameChange()"
              >
                <template #no-data>
                  <v-alert v-if="loadingSelectStatus === 'error'" type="error" text class="ma-0 rounded-0">
                    {{ loadingSelectText }}
                  </v-alert>
                  <div v-else class="pa-4 text-body-2">
                    Veri bulunamadı
                  </div>
                </template>
                <template #progress>
                  <v-alert
                    :type="loadingSelectStatus"
                    :color="!loadingSelectStatus ? 'primary' : null"
                    text
                    class="ma-0 rounded-0"
                  >
                    <template v-if="loadingSelectStatus === null" #prepend>
                      <v-progress-circular
                        class="mr-3"
                        :width="2"
                        :size="25"
                        color="primary"
                        indeterminate
                      ></v-progress-circular>
                    </template>
                    {{ loadingSelectText }}
                  </v-alert>
                </template>
              </v-autocomplete>
            </ValidationProvider>

            <ValidationProvider v-slot="{ errors }" rules="required|min_value:1">
              <QtyInput v-model="formField.qty" :step="formField.birim_ritim || 1" :min="formField.birim_ritim || 1" />

              <span v-if="errors.length" class="error--text">{{ errors[0] }}</span>
            </ValidationProvider>
          </form>
        </ValidationObserver>

        <v-divider />

        <div v-if="TESF > 0">
          <v-alert
            rounded="0"
            type="info"
            text
            color="primary"
            class="text-caption ma-0 d-flex align-center"
          >
            Birim Satış Fiyatı :
            <v-progress-circular
              v-if="priceLoading"
              indeterminate
              color="primary"
              :size="16"
              :width="2"
              class="ml-2"
            />
            <span
              v-else
              class="ml-1 font-weight-bold"
            >{{ formField.price | currency }}</span>
          </v-alert>
          <v-divider />
          <v-alert
            rounded="0"
            type="info"
            text
            color="primary"
            class="text-caption ma-0 d-flex align-center"
          >
            Tavsiye Edilen Perakende Satış Fiyatı :
            <v-progress-circular
              v-if="priceLoading"
              indeterminate
              color="primary"
              :size="16"
              :width="2"
              class="ml-2"
            />
            <span
              v-else
              class="ml-1 font-weight-bold"
            >{{ formField.price_ps | currency }}</span>
          </v-alert>
        </div>
        <div v-if="STOCK_CONTROL">
          <v-alert rounded="0" :type="stockType" text class="text-caption ma-0">
            {{ stockText }}
          </v-alert>
        </div>
      </v-card>
      <v-container v-if="$vuetify.breakpoint.xsOnly" class="pb-16">
        <v-row align="center" no-gutters class="pb-16">
          <v-col cols="4">
            <v-btn plain small block @click="closeSidebar"> Vazgeç </v-btn>
          </v-col>
          <v-col cols="8">
            <v-btn
              color="primary"
              rounded
              x-large
              block
              class="me-3"
              :disabled="stockDisabled || isSubmitting || isAddPending"
              :loading="isSubmitting || isAddPending"
              @click="submitForm"
            >
              Ekle
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
      <template v-if="!$vuetify.breakpoint.xsOnly" v-slot:append>
        <v-container>
          <v-row align="center" no-gutters>
            <v-col cols="4">
              <v-btn plain small block @click="closeSidebar"> Vazgeç </v-btn>
            </v-col>
            <v-col cols="8">
              <v-btn
                color="primary"
                rounded
                x-large
                block
                class="me-3"
                :disabled="stockDisabled || isSubmitting || isAddPending"
                :loading="isSubmitting || isAddPending"
                @click="submitForm"
              >
                Ekle
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
      </template>
    </v-navigation-drawer>
  </div>
</template>

<script>
import { newItemId, useOrderStore } from '@/stores/order'
import useAlertGuard from '@/utils/useAlertGuard'
import { getUserData } from '@/utils/userData'
import { showAppSnackbar } from '@/utils/showSnackbar'
import QtyInput from '@/views/QtyInput.vue'
import axios from '@axios'
import { mdiClose, mdiEyePlus } from '@mdi/js'
import { computed, onMounted, ref, watch } from '@vue/composition-api'

export default {
  components: {
    QtyInput,
  },
  model: {
    prop: 'isAddEditOtherProductsSidebar',
    event: 'update:is-add-edit-other-products-sidebar',
  },
  props: {
    isAddEditOtherProductsSidebar: {
      type: Boolean,
      required: true,
    },
  },
  setup(props, { emit }) {
    const { alertGuard } = useAlertGuard()

    const userData = getUserData()
    const order = useOrderStore()
    const form = ref(null)
    const isAddPending = ref(false)
    const isSubmitting = ref(false)
    const addPendingCount = ref(0)

    const startAddPending = () => {
      addPendingCount.value += 1
      isAddPending.value = true
    }

    const endAddPending = () => {
      addPendingCount.value = Math.max(0, addPendingCount.value - 1)
      if (addPendingCount.value === 0) {
        isAddPending.value = false
      }
    }

    const trackAddPending = promise => {
      startAddPending()
      return promise.finally(endAddPending)
    }

    const formField = ref({
      id: null,
      type: 'other',
      brand: null,
      name: null,
      itemcode: null,
      direction: { text: 'Sağ', value: 'SAĞ', reverse: { text: 'Sol', value: 'SOL' } },
      sph: null,
      cyl: { text: 'a', value: 'a' },
      qty: 1,
      haswarrantycard: '2',
      fullname: null,
      axe: null,
      title: null,
      price: 0,
      price_ps: 0,
      tax: 0,
      discount: [],
      currency: null,
      tax_rate: null,
      prd_code: null,
      reverse: true,
      cmpDiscValueType: null,
      cmpDiscValue: null,
      cmpDiscMsg: null,
      birim_ritim: 1,
      subTotal: 0,
    })
    const formFieldCopy = ref({})
    const formFieldDouble = ref({})

    onMounted(() => {
      formFieldCopy.value = { ...formField.value }
    })

    const items = ref({
      brand: [],
      name: [],
    })
    const itemsCopy = ref({})
    onMounted(() => {
      itemsCopy.value = { ...items.value }
    })

    const loadingSelectStatus = ref(null)
    const loadingSelectText = ref('Lütfen Bekleyin Yükleniyor')

    const nameFocus = () => {
      const params = new URLSearchParams()
      params.append('method', 'getOtherProducts')
      trackAddPending(
        axios
          .post('', params)
          .then(response => {
            loadingSelectStatus.value = null
            loadingSelectText.value = 'Lütfen Bekleyin Yükleniyor'
            if (response.data.error) {
              loadingSelectText.value = response.data.msg
              loadingSelectStatus.value = 'error'

              return
            }

            items.value.name = response.data.detail
          })
          .catch(e => {
            console.error('API Hatası:', e?.message)
            loadingSelectText.value = 'Bir hata oldu tekrar deneyin'
            loadingSelectStatus.value = 'error'
          }),
      )
    }

    const priceLoading = ref(false)

    const clearPrice = () => {
      formField.value.price = 0
      formField.value.price_ps = 0
      formField.value.currency = null
      priceLoading.value = false
    }

    const nameChange = () => {
      formField.value.itemcode = formField.value.name.itemcode
      formField.value.haswarrantycard = formField.value.name.haswarrantycard

      formField.value.sph = null
      formField.value.cyl = null
      items.value.sph = []
      items.value.cyl = []
      clearPrice()
    }

    watch(
      () => ({
        brand: formField.value.brand,
        name: formField.value.name,
      }),
      data => {
        if (data.brand && data.name?.itemcode) {
          const params = new URLSearchParams()
          params.append('method', 'getStockRangeByFilter')
          params.append('itemcode', formField.value.name.itemcode)
          params.append('brand', formField.value.brand)
          trackAddPending(
            axios
              .post('', params)
              .then(response => {
                loadingSelectStatus.value = null
                loadingSelectText.value = 'Lütfen Bekleyin Yükleniyor'

                if (response.data.error) {
                  loadingSelectText.value = response.data.msg
                  loadingSelectStatus.value = 'error'

                  return
                }

                let positiveSPH = []
                let negativeSPH = []
                response.data.detail.sphValues.forEach(item => {
                  if (item >= 0) {
                    positiveSPH.push(item)
                  } else {
                    negativeSPH.push(item)
                  }
                })
                positiveSPH = positiveSPH.sort((a, b) => a - b)
                negativeSPH = negativeSPH.sort((a, b) => b - a)
                const valueSPH = positiveSPH.concat(negativeSPH)

                let positiveCYL = []
                let negativeCYL = []
                response.data.detail.cylValues.forEach(item => {
                  if (item >= 0) {
                    positiveCYL.push(item)
                  } else {
                    negativeCYL.push(item)
                  }
                })
                positiveCYL = positiveCYL.sort((a, b) => a - b)
                negativeCYL = negativeCYL.sort((a, b) => b - a)
                const valueCYL = positiveCYL.concat(negativeCYL)

                const sph = valueSPH.map(key => ({
                  text:
                    key / 100 > 0
                      ? `+${(key / 100).toFixed(2)}`
                      : key / 100 == 0
                      ? '0.00'
                      : `-${((key / 100) * -1).toFixed(2)}`,
                  value: key,
                }))
                const cyl = valueCYL.map(key => ({
                  text:
                    key / 100 > 0
                      ? `+${(key / 100).toFixed(2)}`
                      : key / 100 == 0
                      ? '0.00'
                      : `-${((key / 100) * -1).toFixed(2)}`,
                  value: key,
                }))

                items.value.cyl = response.data.detail.cylValues
                items.value.sph = response.data.detail.sphValues

                formField.value.cyl = items.value.cyl[0]
              })
              .catch(e => {
                console.error('API Hatası:', e?.message)
                loadingSelectText.value = 'Bir hata oldu tekrar deneyin'
                loadingSelectStatus.value = 'error'
              }),
          )
        }
      },
    )

    const prdcode = computed(() => {
      const { itemcode } = formField.value

      return `${itemcode}`
    })

    const TESF = computed(() => {
      if (formField.value.itemcode !== null) {
        return 1
      }

      return 0
    })

    const lastPriceKey = ref(null)
    const lastQueriedQty = ref(null)
    watch(
      [() => formField.value.itemcode, () => prdcode.value],
      ([itemcode, prdCodeValue]) => {
        if (itemcode === null) {
          lastPriceKey.value = null
          clearPrice()
          return
        }

        if (!prdCodeValue || lastPriceKey.value === prdCodeValue) {

          return
        }
        lastPriceKey.value = prdCodeValue
        formField.value.price = 0
        formField.value.price_ps = 0
        formField.value.currency = null
        priceLoading.value = true

        // İlk fiyat sorgusu her zaman qty=1 ile gönderilir; kullanıcının o an
        // formda girmiş olabileceği miktar bu sorguyu etkilemez.
        const params = new URLSearchParams()
        params.append('method', 'getProductPrice')
        params.append('prdcode', prdCodeValue)
        params.append('prdtype', 'other')
        params.append('qty', 1)
        trackAddPending(
          axios
            .post('', params)
            .then(response => {
              if (response.data.error) {
                showAppSnackbar(response.data.msg, 'warning')
                cancelPriceSelection()

                return
              }
              formField.value.title = response.data.detail.title2
              formField.value.price = response.data.detail.price
              formField.value.price_ps = response.data.detail.price_ps
              formField.value.currency = response.data.detail.price_currency
              formField.value.tax_rate = response.data.detail.tax_rate
              formField.value.taxAmount = response.data.detail.taxAmount
              formField.value.netAmount = response.data.detail.netAmount
              formField.value.subTotal = response.data.detail.subTotal
              formField.value.prd_code = response.data.detail.prd_code
              formField.value.discount = []
              if (response.data.detail.discAmount) {
                formField.value.discount.push(response.data.detail.discAmount)
              }

              // birim_ritim yalnızca QtyInput'un min/step değeri için saklanır;
              // kullanıcının forma girdiği qty değeri buradan etkilenmez.
              formField.value.birim_ritim = response.data.prd.birim_ritim || 1
              lastQueriedQty.value = 1
            })
            .catch(() => {
              showAppSnackbar('Bir hata oldu tekrar deneyin', 'error')
              cancelPriceSelection()
            })
            .finally(() => {
              priceLoading.value = false
            }),
        )
      },
    )


    const stockText = ref('')
    const stockType = ref(null)
    const stockStatus = ref(false)
    const stockDisabled = ref(false)
    const stockDetail = ref({ merkez: 0, depo: 0, toplam: 0 })
    const STOCK_CONTROL = computed(() => {
      if (formField.value.itemcode === null) {
        return false
      }

      return stockStatus.value
    })

    const lastStockKey = ref(null)

    const cancelPriceSelection = () => {
      lastPriceKey.value = null
      lastStockKey.value = null
      formField.value.name = null
      formField.value.itemcode = null
      clearPrice()
      stockStatus.value = false
      stockDisabled.value = false
    }

    watch(
      [() => formField.value.itemcode, () => prdcode.value],
      ([itemcode, prdCodeValue]) => {
        if (itemcode === null) {
          lastStockKey.value = null
          stockStatus.value = false
          return
        }

        if (!prdCodeValue || lastStockKey.value === prdCodeValue) {

          return
        }
        lastStockKey.value = prdCodeValue

        const params = new URLSearchParams()
        params.append('method', 'getPrdStockByOffice')
        params.append('prdcode', prdCodeValue)
        params.append('prdtype', 'other')
        trackAddPending(
          axios
            .post('', params)
            .then(response => {
              if (response.data.error) {
                stockStatus.value = false
                stockDisabled.value = true
                stockText.value = response.data.msg || 'Stok bilgisi alınamadı'
                stockType.value = 'error'

                return
              }

              formField.value.qty = formField.value.birim_ritim || 1

              stockDetail.value.merkez = 0
              stockDetail.value.depo = 0

              response.data.detail.forEach(item => {
                if (item.WarehouseCode == '3407' || item.WarehouseCode == '3400' || item.WarehouseCode == 'PR_OPK_MKZ') {
                  stockDetail.value.merkez += Number(item.InventoryQty1) || 0
                } else {
                  stockDetail.value.depo += Number(item.InventoryQty1) || 0
                }
              })
              stockDetail.value.toplam = stockDetail.value.depo + stockDetail.value.merkez

              stockFn(stockDetail.value, formField.value.qty)
              stockStatus.value = true
            })
            .catch(e => {
              console.error('API Hatası:', e?.message)
              stockStatus.value = false
              stockDisabled.value = true
              stockText.value = 'Bir hata oldu tekrar deneyin'
              stockType.value = 'error'
            }),
        )
      },
    )

    watch(
      [() => stockDetail.value, () => formField.value.qty],
      ([newStock, newQty]) => {
        stockFn(newStock, newQty)
      },
      { deep: true },
    )

    const stockFn = (stock, qty) => {
      stockDisabled.value = false

      /*  if (formField.value.qty > 20) {
        stockText.value = 'Şubede ile temasa geçiniz'
        stockType.value = 'error'
        stockDisabled.value = true

        return
      } */
      if (stock.depo >= qty) {
        // stockText.value = 'Depodaki Ürün Sayısı ' + stock.depo
        stockText.value = 'Şubede stok var'
        stockType.value = 'success'
      } else if (stock.depo == 0 && stock.toplam >= qty) {
        // stockText.value = 'Bu ürün merkezden gelecek'
        stockText.value = 'Şubede stok yok , merkezde Var'
        stockType.value = 'warning'
      } else if (stock.depo < qty && stock.toplam >= qty) {
        // stockText.value = `Bu üründen ${stock.depo} adet depodan ${qty - stock.depo} adeti merkezden gelecek`
        stockText.value = 'Şubede stok yok , merkezde var'
        stockType.value = 'warning'
      } else if (stock.toplam < qty) {
        stockDisabled.value = true

        // stockText.value = `Bu kadar ürün yok en fazla ${stock.toplam} adet sipariş edebilirsiniz.`
        stockText.value = 'Stok hiç bir yerde yok.'
        stockType.value = 'error'
      }
    }

    const closeSidebar = () => {
      emit('update:is-add-edit-other-products-sidebar', false)
      dialog.value = false
      addPendingCount.value = 0
      lastPriceKey.value = null
      lastStockKey.value = null
      priceLoading.value = false
      isAddPending.value = false
      setTimeout(() => {
        if (props.isAddEditOtherProductsSidebar) return // panel bu arada yeniden açıldıysa kullanıcının girdiği veri silinmesin
        form.value.reset()

        formField.value = { ...formFieldCopy.value }
        formField.value.discount = []
        items.value = { ...itemsCopy.value }
      }, 500)
    }

    const dialog = ref(false)
    const submitForm = async () => {
      if (isSubmitting.value || isAddPending.value) return
      isSubmitting.value = true
      const isValid = await form.value.validate()
      if (!isValid) {
        isSubmitting.value = false
        return
      }

      // Miktar, son sorgulanan değerden değiştiyse fiyat tekrar sorgulanır;
      // aksi halde (örn. hiç değiştirilmediyse) gereksiz istek atılmaz.
      if (formField.value.qty !== lastQueriedQty.value) {
        const priceParams = new URLSearchParams()
        priceParams.append('method', 'getProductPrice')
        priceParams.append('prdcode', formField.value.prd_code)
        priceParams.append('prdtype', 'other')
        priceParams.append('qty', formField.value.qty)

        const priceResponse = await axios.post('', priceParams).catch(() => null)
        if (!priceResponse?.data || priceResponse.data.error) {
          showAppSnackbar(priceResponse?.data?.msg || 'Fiyat güncellenemedi, lütfen tekrar deneyin', 'warning')
          isSubmitting.value = false

          return
        }
        formField.value.price = priceResponse.data.detail.price
        formField.value.taxAmount = priceResponse.data.detail.taxAmount
        formField.value.netAmount = priceResponse.data.detail.netAmount
        formField.value.subTotal = priceResponse.data.detail.subTotal
        formField.value.discount = []
        if (priceResponse.data.detail.discAmount) {
          formField.value.discount.push(priceResponse.data.detail.discAmount)
        }
      }

      const params = new URLSearchParams()
      params.append('method', 'getCampaign')
      params.append('prd_code ', formField.value.prd_code)
      params.append('qty', formField.value.qty)

      trackAddPending(
        axios.post('', params).then(response => {
          if (response.data.error === false) {
            // Kampanya indirimi backend tarafından fiyata (discAmount/netAmount) zaten
            // yansıtılmış geliyor; cmpDiscValue, dip iskonto satırında ayrıca göstermek
            // için saklanır (Toplam İskonto'dan çıkarılır).
            formField.value.cmpDiscValue = response.data.cmpDiscValue
            formField.value.cmpDiscValueType = response.data.cmpDiscValueType
            formField.value.cmpDiscMsg = response.data.msg
          }
        }),
      )
        .catch(() => {
          showAppSnackbar('Kampanya bilgisi alınamadı, ürün kampanyasız eklendi.', 'warning')
        })
        .finally(() => {
          formField.value.id = newItemId()
          order.addItem(formField.value)

          /*   console.log('reverse ' + formField.value.reverse)
          if (formField.value.reverse) {
            dialog.value = true
          } else { */
          closeSidebar()

          // }
          isSubmitting.value = false
      })
    }
    watch(
      () => formField.value.name,
      newName => {
        if (newName?.birim_ritim) {
          formField.value.qty = newName.birim_ritim
        }
      },
    )

    return {
      alertGuard,
      order,
      form,
      formField,
      items,
      TESF,
      priceLoading,
      stockStatus,
      stockDetail,
      stockDisabled,
      stockText,
      stockType,
      STOCK_CONTROL,
      loadingSelectText,
      loadingSelectStatus,

      nameFocus,
      nameChange,

      closeSidebar,
      dialog,
      submitForm,
      isAddPending,
      isSubmitting,
      icons: {
        mdiClose,
        mdiEyePlus,
      },
    }
  },
}
</script>
